import * as React from "react";
import { cn } from "../../../helpers";
import styles from "./Input.module.scss";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: React.ReactNode;
    inputSize?: "small";
    errorText?: string;
    errorTextClassName?: string;
    hasError?: boolean;
    hidden?: boolean;
    isRequired?: boolean;
    isOptional?: boolean;
    inputContainerClassName?: string;
    className?: string;
    endIcon?: React.ReactNode;
    inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            errorText,
            hasError,
            hidden,
            isRequired,
            isOptional,
            inputSize,
            inputContainerClassName,
            className,
            errorTextClassName,
            endIcon,
            inputClassName,
            ...props
        },
        ref
    ) => {
        if (hidden) return null;

        const inputBaseClass = cn(styles.input, hasError && styles.error, className);

        const wrapper = (
            <div className={cn(styles.wrapper, inputContainerClassName)}>
                <div className={styles.inputContainer}>
                    <input
                        ref={ref}
                        className={cn(inputBaseClass, inputClassName, {
                            [styles.small]: inputSize === "small",
                        })}
                        {...props}
                    />
                    {endIcon && <div className={styles.endIcon}>{endIcon}</div>}
                </div>
                {hasError && errorText && (
                    <span className={cn(styles.errorText, errorTextClassName)}>
                        {errorText}
                    </span>
                )}
            </div>
        );

        if (!label) return wrapper;

        return (
            <div className={cn(styles.inputContainer)}>
                <div className={styles.container}>
                    <label htmlFor={props.name} className={styles.label}>
                        {label}
                        {isOptional && (
                            <span className={styles.optional} aria-label="optional">
                                (optional)
                            </span>
                        )}
                        {isRequired && (
                            <span className={styles.required} aria-label="required">
                                *
                            </span>
                        )}
                    </label>
                    {wrapper}
                </div>
            </div>
        );
    }
);

Input.displayName = "Input";

export { Input };
